import {
  type FetchArgs,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import {
  createSessionV2,
  joinSessionV2,
  createPreCallTestSession,
  startLiveCaptions,
  stopLiveCaptions,
} from './sessionApi.helper';
import { getLogger } from '../libs/logger';
import type {
  PreCallTestSession,
  StartLiveCaptions,
  StopLiveCaptions,
  WebRtcCreateSessionV2Input,
  WebRtcJoinSessionV2Input,
  WebRtcSessionV2,
  WebRtcStartLiveCaptionsInput,
  WebRtcStopLiveCaptionsInput,
} from '../services/graphQlTypes.generated';

import type { ClientError } from 'graphql-request';
import { type ProviderAuthCredentials } from './provider/authService';

const logger = getLogger('sessionApiGql');

const getError = (error: ClientError): Error | undefined => {
  return error.response.errors?.[0]?.extensions['originalError'] as Error;
};

export type WebRtcCreateSessionV2InputWithCredentials =
  WebRtcCreateSessionV2Input & {
    credentials: ProviderAuthCredentials;
  };

export type WebRtcJoinSessionV2InputWithCredentials =
  WebRtcJoinSessionV2Input & {
    credentials: ProviderAuthCredentials;
  };

export const sessionApiService = createApi({
  reducerPath: 'sessionApi',
  baseQuery: (args: string | FetchArgs, api, extraOptions) =>
    fetchBaseQuery({ baseUrl: '/graphql' })(args, api, extraOptions),
  endpoints: (builder) => ({
    createSessionV2: builder.query<
      WebRtcSessionV2,
      WebRtcCreateSessionV2InputWithCredentials
    >({
      queryFn: async (arg) => {
        try {
          const response = await createSessionV2(arg);

          if (response) {
            logger.info('[createSessionV2] create session request success');
            return {
              data: response,
            };
          }
        } catch (err) {
          return {
            error: {
              error:
                '[createSessionV2] Request to create session gql endpoint failed',
              status: 400,
              data: getError(err as ClientError),
            },
          };
        }

        return {
          error: {
            error: 'Failed to createSessionV2',
            status: 500,
          },
        };
      },
    }),
    joinSessionV2: builder.query<
      WebRtcSessionV2,
      WebRtcJoinSessionV2InputWithCredentials
    >({
      queryFn: async (arg) => {
        try {
          const response = await joinSessionV2(arg);

          if (response) {
            logger.info('[joinSessionV2] join session request success');
            return {
              data: response,
            };
          }
        } catch (err) {
          return {
            error: {
              error: 'Failed to joinSessionV2',
              status: 400,
              data: getError(err as ClientError),
            },
          };
        }

        return {
          error: {
            error: 'Failed to joinSessionV2',
            status: 500,
          },
        };
      },
    }),
    createPreCallTestSession: builder.query<PreCallTestSession, void>({
      queryFn: async () => {
        try {
          const response = await createPreCallTestSession();

          if (response) {
            return { data: response };
          }
        } catch (err) {
          return {
            error: {
              error: 'Failed to createPreCallTestSession',
              status: 400,
              data: getError(err as ClientError),
            },
          };
        }

        return {
          error: {
            error: 'Failed to createPreCallTestSession',
            status: 500,
          },
        };
      },
    }),
    startLiveCaptions: builder.mutation<
      StartLiveCaptions,
      WebRtcStartLiveCaptionsInput
    >({
      queryFn: async (arg) => {
        try {
          const response = await startLiveCaptions(arg);

          if (response) {
            logger.info(
              '[startLiveCaptions] start live captions request success',
            );
            return {
              data: response,
            };
          }
        } catch (err) {
          return {
            error: {
              error: 'Failed to startLiveCaptions',
              status: 400,
              data: getError(err as ClientError),
            },
          };
        }

        return {
          error: {
            error: 'Failed to startLiveCaptions',
            status: 500,
          },
        };
      },
    }),
    stopLiveCaptions: builder.mutation<
      StopLiveCaptions,
      WebRtcStopLiveCaptionsInput
    >({
      queryFn: async (arg) => {
        try {
          const response = await stopLiveCaptions(arg);

          if (response) {
            logger.info(
              '[stopLiveCaptions] stop live captions request success',
            );
            return {
              data: response,
            };
          }
        } catch (err) {
          return {
            error: {
              error: 'Failed to stopLiveCaptions',
              status: 400,
              data: getError(err as ClientError),
            },
          };
        }

        return {
          error: {
            error: 'Failed to stopLiveCaptions',
            status: 500,
          },
        };
      },
    }),
  }),
});

export const {
  useStartLiveCaptionsMutation,
  useStopLiveCaptionsMutation,
  useLazyCreatePreCallTestSessionQuery,
} = sessionApiService;
